import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Grid, InputBase, Typography } from "@mui/material";
import { CustomChart, ChartSeriesConfig, ChartGroupConfig, ChartValueAxisConfig } from "@/components/chart/CustomChart";
import { OfficeWithPersona } from "@/models/goFurther";
import {
  FONT_SIZE,
  FONT_WEIGHT,
  TEXT_COLOR,
  BACKGROUND_COLOR,
  COLOR_EV_BAR,
  COLOR_EV_LINE,
  COLOR_LPG_BAR,
  COLOR_LPG_LINE,
  AXIS_LEFT,
  AXIS_RIGHT,
} from "../GoFurtherStyles";
import { SectionGridItemCard, SecondaryPanel } from "./GoFurtherPrimitives";
import { formatYearMonthJA } from "@/util/datetime";
import { formatMileageAxisTick, formatRevenueAxisTick, type ComparisonDataItem } from "../GoFurtherUtils";
import { createGoFurtherTooltip } from "./GoFurtherChartTooltip";

interface GoFurtherOfficeSimulationSectionProps {
  selectedOffice: OfficeWithPersona | null;
  chartData: ComparisonDataItem[];
  baseNonEvRevenue: number | null;
  onApplyBaseNonEvRevenue: (revenue: number) => void;
}

const EMPTY_VALUE = "---";
const YEN_PER_MAN_EN = 10000;
const MAX_BASE_NON_EV_REVENUE_DIGITS = 3;
const BUBBLE_TIP_MARGIN = 4;
const ARROW_BUBBLE_TIP_SIDE_PADDING = 12;
const ARROW_HEAD_HALF_WIDTH = 6;
const ARROW_HEAD_HEIGHT = 6;

const comparisonSeries: ChartSeriesConfig[] = [
  { dataKey: "evRevenue", name: "EV 営収", type: "bar", color: COLOR_EV_BAR, yAxisId: AXIS_LEFT },
  { dataKey: "lpgRevenue", name: "LPG 営収", type: "bar", color: COLOR_LPG_BAR, yAxisId: AXIS_LEFT },
  { dataKey: "evMileage", name: "EV 走行距離", type: "line", color: COLOR_EV_LINE, yAxisId: AXIS_RIGHT },
  { dataKey: "lpgMileage", name: "LPG 走行距離", type: "line", color: COLOR_LPG_LINE, yAxisId: AXIS_RIGHT },
];

const comparisonGroups: ChartGroupConfig[] = [
  { title: "EV", dataKeys: ["evRevenue", "evMileage"] },
  { title: "LPG", dataKeys: ["lpgRevenue", "lpgMileage"] },
];

const comparisonAxes: ChartValueAxisConfig[] = [
  {
    id: AXIS_LEFT,
    orientation: "left",
    tickFormatter: formatRevenueAxisTick,
  },
  {
    id: AXIS_RIGHT,
    orientation: "right",
    tickFormatter: formatMileageAxisTick,
  },
];

const ComparisonTooltip = createGoFurtherTooltip({
  roundValue: true,
  getUnit: (dataKey) => (dataKey.toLowerCase().includes("revenue") ? "円" : "km"),
});

type ArrowGeometry = {
  path: string;
  headRoof: string;
  bubble: string;
  width: number;
  height: number;
};

const buildArrowGeometry = (
  panelRect: DOMRect,
  buttonRect: DOMRect,
  targetRect: DOMRect,
  simulationPanelRect: DOMRect,
): ArrowGeometry => {
  const startX = buttonRect.left + buttonRect.width / 2 - panelRect.left;
  const startY = buttonRect.top - panelRect.top - 8;
  const tipX = targetRect.left + targetRect.width / 2 - panelRect.left;
  const bubbleTipX = tipX;
  const bubbleTipY = targetRect.bottom - panelRect.top + BUBBLE_TIP_MARGIN;
  const tipY = bubbleTipY + ARROW_BUBBLE_TIP_SIDE_PADDING;
  const simulationPanelTopY = simulationPanelRect.top - panelRect.top;
  const bubbleBaseY = simulationPanelTopY;
  const bubbleBottomY = bubbleBaseY + 12;
  const bubbleHalfNeck = 10;
  const bubbleHalfWidth = 28;
  const bubbleTipSidePadding = ARROW_BUBBLE_TIP_SIDE_PADDING;
  const bubbleShoulderOffsetX = bubbleHalfNeck + bubbleTipSidePadding;

  const bendY = Math.min(startY - 24, tipY + 28);
  const endY = tipY + ARROW_HEAD_HEIGHT;
  const cornerRadius = 10;

  const verticalDir1 = bendY >= startY ? 1 : -1;
  const horizontalDir = tipX >= startX ? 1 : -1;
  const verticalDir2 = endY >= bendY ? 1 : -1;

  const firstCornerRadius = Math.min(cornerRadius, Math.abs(bendY - startY), Math.abs(tipX - startX));
  const secondCornerRadius = Math.min(cornerRadius, Math.abs(tipX - startX), Math.abs(endY - bendY));

  const path = [
    `M ${startX} ${startY}`,
    `L ${startX} ${bendY - verticalDir1 * firstCornerRadius}`,
    `Q ${startX} ${bendY} ${startX + horizontalDir * firstCornerRadius} ${bendY}`,
    `L ${tipX - horizontalDir * secondCornerRadius} ${bendY}`,
    `Q ${tipX} ${bendY} ${tipX} ${bendY + verticalDir2 * secondCornerRadius}`,
    `L ${tipX} ${endY}`,
  ].join(" ");
  const headRoof = `${tipX - ARROW_HEAD_HALF_WIDTH},${tipY + ARROW_HEAD_HEIGHT} ${tipX},${tipY} ${tipX + ARROW_HEAD_HALF_WIDTH},${tipY + ARROW_HEAD_HEIGHT}`;

  const bubbleAnchorX = bubbleTipX;

  const bubble = [
    `M ${bubbleAnchorX - bubbleHalfWidth} ${bubbleBaseY}`,
    `L ${bubbleAnchorX - bubbleShoulderOffsetX} ${bubbleBaseY}`,
    `L ${bubbleAnchorX} ${bubbleTipY}`,
    `L ${bubbleAnchorX + bubbleShoulderOffsetX} ${bubbleBaseY}`,
    `L ${bubbleAnchorX + bubbleHalfWidth} ${bubbleBaseY}`,
    `L ${bubbleAnchorX + bubbleHalfWidth} ${bubbleBottomY}`,
    `L ${bubbleAnchorX - bubbleHalfWidth} ${bubbleBottomY}`,
    "Z",
  ].join(" ");

  return {
    path,
    headRoof,
    bubble,
    width: panelRect.width,
    height: panelRect.height,
  };
};

const GoFurtherOfficeSimulationSection: React.FC<GoFurtherOfficeSimulationSectionProps> = ({
  selectedOffice,
  chartData,
  baseNonEvRevenue,
  onApplyBaseNonEvRevenue,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const simulationPanelRef = useRef<HTMLDivElement | null>(null);
  const applyButtonRef = useRef<HTMLButtonElement | null>(null);
  const trialRevenueRef = useRef<HTMLSpanElement | null>(null);
  const [arrowPath, setArrowPath] = useState<string>("");
  const [arrowHeadRoof, setArrowHeadRoof] = useState<string>("");
  const [arrowBubble, setArrowBubble] = useState<string>("");
  const [svgSize, setSvgSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const updateArrow = useCallback(() => {
    const panelEl = panelRef.current;
    const simulationPanelEl = simulationPanelRef.current;
    const buttonEl = applyButtonRef.current;
    const targetEl = trialRevenueRef.current;

    if (!panelEl || !simulationPanelEl || !buttonEl || !targetEl) {
      setArrowPath("");
      setArrowHeadRoof("");
      setArrowBubble("");
      return;
    }

    const panelRect = panelEl.getBoundingClientRect();
    const simulationPanelRect = simulationPanelEl.getBoundingClientRect();
    const buttonRect = buttonEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();

    const geometry = buildArrowGeometry(panelRect, buttonRect, targetRect, simulationPanelRect);
    setSvgSize({ width: geometry.width, height: geometry.height });
    setArrowPath(geometry.path);
    setArrowHeadRoof(geometry.headRoof);
    setArrowBubble(geometry.bubble);
  }, []);

  useLayoutEffect(() => {
    updateArrow();

    const panelEl = panelRef.current;
    const simulationPanelEl = simulationPanelRef.current;
    const buttonEl = applyButtonRef.current;
    const targetEl = trialRevenueRef.current;
    if (!panelEl || !simulationPanelEl || !buttonEl || !targetEl) return;

    const observer = new ResizeObserver(() => {
      updateArrow();
    });

    observer.observe(panelEl);
    observer.observe(simulationPanelEl);
    observer.observe(buttonEl);
    observer.observe(targetEl);

    window.addEventListener("resize", updateArrow);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateArrow);
    };
  }, [updateArrow]);

  const latestSummary = useMemo(() => {
    const latest = chartData.length > 0 ? chartData[chartData.length - 1] : null;
    const latestMonth = formatYearMonthJA(latest?.month) || "----年--月";
    const evMileage = Number(latest?.evMileage ?? 0);
    const lpgMileage = Number(latest?.lpgMileage ?? 0);

    const lpgMileagePerVehicle = lpgMileage > 0 ? Math.round(lpgMileage).toLocaleString("ja-JP") : EMPTY_VALUE;
    const evMileagePerVehicle = evMileage > 0 ? Math.round(evMileage).toLocaleString("ja-JP") : EMPTY_VALUE;
    const lpgRevenueYen = (baseNonEvRevenue ?? 0) * YEN_PER_MAN_EN;
    const monthlyUpByFormula =
      lpgMileage > 0 ? (lpgRevenueYen / lpgMileage) * (lpgMileage - evMileage) : Number.NaN;
    const monthlyUp = Number.isFinite(monthlyUpByFormula) && monthlyUpByFormula > 0
      ? Math.round(monthlyUpByFormula).toLocaleString("ja-JP")
      : EMPTY_VALUE;

    const evMileageBeforeExclusionRaw = latest?.evMileageBeforeExclusion;
    const evMileageBeforeExclusionPerVehicle =
      evMileageBeforeExclusionRaw != null && evMileageBeforeExclusionRaw > 0
        ? Math.round(evMileageBeforeExclusionRaw).toLocaleString("ja-JP")
        : null;

    return {
      latestMonth,
      lpgMileagePerVehicle,
      evMileagePerVehicle,
      evMileageBeforeExclusionPerVehicle,
      monthlyUp,
    };
  }, [baseNonEvRevenue, chartData]);

  const officeName = selectedOffice?.name || "-";
  const [baseNonEvRevenueInput, setBaseNonEvRevenueInput] = useState<string>("");

  useEffect(() => {
    setBaseNonEvRevenueInput(baseNonEvRevenue != null ? String(baseNonEvRevenue) : "");
  }, [baseNonEvRevenue]);

  const parsedBaseNonEvRevenue = Number(baseNonEvRevenueInput);
  const canApplyBaseNonEvRevenue = Number.isFinite(parsedBaseNonEvRevenue) && parsedBaseNonEvRevenue > 0;

  const handleBaseNonEvRevenueInputChange = (nextValue: string) => {
    const sanitizedValue = nextValue.replace(/[^0-9]/g, "").slice(0, MAX_BASE_NON_EV_REVENUE_DIGITS);
    setBaseNonEvRevenueInput(sanitizedValue);
  };

  const handleApplyBaseNonEvRevenue = () => {
    if (!canApplyBaseNonEvRevenue) return;
    onApplyBaseNonEvRevenue(parsedBaseNonEvRevenue);
  };

  return (
    <SectionGridItemCard
      title={
        <Typography component="h2" sx={{ fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5 }}>
          {officeName}様の<br />
          <Typography
            component="span"
            sx={{ color: TEXT_COLOR.SUCCESS, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline4 }}
          >
            EVの稼働データと営収シミュレーション
          </Typography>
        </Typography>
      }
    >
      <Grid item xs={12}>
        <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1 }}>
          EVでもLPGやガソリン車と同水準の稼働・営収を実現できている事業者様が増えています。
          <br />
          {officeName}様でEVをLPG並みに稼働頂いた場合、以下の営収向上の可能性があります。
        </Typography>
        <Typography sx={{ mt: 1, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
          ※営収は走行距離から算出しており、営収を保障するものではございませんので、参考としてお考えください。
        </Typography>
        <Typography sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
          EVでLPG同等の走行距離を実現している事業者様では、走行距離増加に応じて営収も増加しております。弊社では、走行距離増加の支援をさせて頂きます。
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <SecondaryPanel sx={{ p: 3, borderRadius: 3, border: "none" }}>
          <Box ref={panelRef} sx={{ position: "relative", bgcolor: BACKGROUND_COLOR.WHITE, borderRadius: 2, p: 3 }}>
            {arrowPath && arrowHeadRoof && arrowBubble && (
              <Box
                component="svg"
                viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
                preserveAspectRatio="xMinYMin meet"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  overflow: "visible",
                  zIndex: 1,
                }}
              >
                <path d={arrowBubble} fill={BACKGROUND_COLOR.SECONDARY} />
                <path d={arrowPath} fill="none" stroke={TEXT_COLOR.SECONDARY} strokeWidth={1.5} strokeDasharray="4 5" />
                <polyline
                  points={arrowHeadRoof}
                  fill="none"
                  stroke={TEXT_COLOR.SECONDARY}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            )}

            <Grid container spacing={5}>
              <Grid item xs={5.5} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>
                    稼働データ
                  </Typography>
                  <Typography sx={{ mt: 2, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.subtitle2, fontWeight: FONT_WEIGHT.BOLD }}>
                    月間走行距離平均
                    <Typography component="span" sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption, ml: 1 }}>
                      {latestSummary.latestMonth}データ
                    </Typography>
                  </Typography>
                  <Grid container sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body2 }}>LPG</Typography>
                      <Typography sx={{ color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.headline4, fontWeight: FONT_WEIGHT.BOLD }}>
                        {latestSummary.lpgMileagePerVehicle ?? EMPTY_VALUE}
                        <Typography component="span" sx={{ ml: 1, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.NORMAL }}>
                          km/台
                        </Typography>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body2 }}>EV</Typography>
                      <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4, fontWeight: FONT_WEIGHT.BOLD }}>
                        {latestSummary.evMileagePerVehicle ?? EMPTY_VALUE}
                        <Typography component="span" sx={{ ml: 1, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.NORMAL }}>
                          km/台
                        </Typography>
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                {latestSummary.evMileageBeforeExclusionPerVehicle != null && (
                  <Typography sx={{ mt: 0.5, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
                    ※EV走行距離は未稼働・低稼働を除外（除外前：{latestSummary.evMileageBeforeExclusionPerVehicle} km/台）
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6.5} sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.headline6, fontWeight: FONT_WEIGHT.BOLD }}>
                    営収シミュレーション
                  </Typography>
                  <Typography sx={{ mt: 2, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.subtitle2, fontWeight: FONT_WEIGHT.BOLD }}>
                    EVの稼働をLPG並みに引き上げた場合
                  </Typography>
                  <Typography sx={{ mt: 1, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body2 }}>
                    月間
                  </Typography>
                  <Typography sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.headline4, fontWeight: FONT_WEIGHT.BOLD }}>
                    {latestSummary.monthlyUp ?? EMPTY_VALUE}
                    <Typography component="span" sx={{ ml: 1, color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body2, fontWeight: FONT_WEIGHT.NORMAL }}>
                      円/台 UPの可能性
                    </Typography>
                  </Typography>
                </Box>
                <Typography sx={{ mt: 0, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
                  LPG月間営収
                  <Typography
                    ref={trialRevenueRef}
                    component="span"
                    sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.body2, mx: 0.25 }}
                  >
                    {baseNonEvRevenue != null ? Math.round(baseNonEvRevenue).toLocaleString("ja-JP") : EMPTY_VALUE}
                    <Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: FONT_SIZE.body2, ml: 0.25 }}>
                      万円/台
                    </Typography>
                  </Typography>
                  として試算
                </Typography>
              </Grid>
            </Grid>

            <Box ref={simulationPanelRef} sx={{ mt: 2.5, bgcolor: BACKGROUND_COLOR.SECONDARY, borderRadius: 2, p: 3 }}>
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={7.5}>
                  <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.subtitle2, fontWeight: FONT_WEIGHT.BOLD }}>
                    営収シミュレーションについて
                  </Typography>
                  <Typography component="div" sx={{ mt: 1, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption, width: "436px" }}>
                    <p>
                      貴社の営収データをご入力いただいてのシミュレーションが可能です。
                      <br />
                      初期値は、LPGの月間営収を仮に「50万円」とした場合で計算しています。
                    </p>
                    <p>
                      EVでLPG同等の走行距離を実現している事業者様では、走行距離増加に応じて営収も増加しております。
                      <br />
                      営収は走行距離から算出しており、営収を保障するものではございませんので、参考としてお考えください。
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      * 計算式：営収シミュレーション＝(LPG営収÷LPG走行距離)×(LPG走行距離ーEV走行距離)
                    </p>
                  </Typography>
                </Grid>

                <Grid item xs={4.5} sx={{ p: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1 }}>
                    貴社のLPG月間営収
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ bgcolor: BACKGROUND_COLOR.WHITE, border: `2px solid ${BACKGROUND_COLOR.BORDER_DARK}`, borderRadius: 2, px: 1, py: 0.5, width: 80, fontSize: FONT_SIZE.subtitle1 }}>
                      <InputBase
                        value={baseNonEvRevenueInput}
                        onChange={(e) => handleBaseNonEvRevenueInputChange(e.target.value)}
                        inputProps={{
                          "aria-label": "貴社のLPG月間営収",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          maxLength: MAX_BASE_NON_EV_REVENUE_DIGITS,
                          style: { padding: "4px 0" },
                        }}
                        sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1, fontWeight: FONT_WEIGHT.BOLD, width: "100%" }}
                      />
                    </Box>
                    <Typography sx={{ color: TEXT_COLOR.PRIMARY, fontSize: FONT_SIZE.body1 }}>万円/台</Typography>
                    <Button
                      ref={applyButtonRef}
                      variant="contained"
                      disableElevation
                      onClick={handleApplyBaseNonEvRevenue}
                      disabled={!canApplyBaseNonEvRevenue}
                      sx={{
                        borderRadius: 99,
                        px: 2,
                        py: 1,
                        minWidth: 0,
                        bgcolor: BACKGROUND_COLOR.PRIMARY,
                        color: TEXT_COLOR.CONTRAST,
                        fontSize: FONT_SIZE.body2,
                        fontWeight: FONT_WEIGHT.BOLD,
                        textTransform: "none",
                        ml: 1,
                      }}
                    >
                      反映する
                    </Button>
                  </Box>
                  <Typography sx={{ mt: 1, color: TEXT_COLOR.SECONDARY, fontSize: FONT_SIZE.caption }}>
                    初期値の50万円は仮数値となります。
                    <br />
                    実際の数字をご入力ください。
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </SecondaryPanel>
      </Grid>

      <Grid item xs={12}>
        <CustomChart
          title={
            <Typography sx={{ p: 2, fontSize: FONT_SIZE.headline6 }}>
              {officeName}様の
              <Typography component="span" sx={{ color: TEXT_COLOR.SUCCESS, fontSize: "inherit" }}>
                EVとLPGの稼働・営収推移
              </Typography>
              (月別・車両1台あたり)
            </Typography>
          }
          description={"走行距離は実績、営収は走行距離から算出したシミュレーションとなります。"}
          data={chartData}
          series={comparisonSeries}
          groups={comparisonGroups}
          CustomTooltip={ComparisonTooltip}
          categoryAxis={{ dataKey: "month" }}
          valueAxes={comparisonAxes}
          height={300}
        />
      </Grid>
    </SectionGridItemCard>
  );
};

export default GoFurtherOfficeSimulationSection;
